import React from 'react'
import { useForm } from 'react-hook-form'
import { ModalWrapper } from 'Components'
import { handleError, handleSuccess } from 'Helpers'
import fillingStationsService from 'BoostXpressAdmin/Services/fillingStationsService'
import './LinkFuelFactModal.scss'

interface LinkFuelFactModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  stationId: string
  station?: any
}

interface LinkFuelFactFormData {
  fuelFactCompanyId: string
  fuelFactCompanyBranchId: string
}

const LinkFuelFactModal: React.FC<LinkFuelFactModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  stationId,
  station
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset
  } = useForm<LinkFuelFactFormData>()

  React.useEffect(() => {
    if (station && isOpen) {
      reset({
        fuelFactCompanyId: station.fuelFactCompanyId || '',
        fuelFactCompanyBranchId: station.fuelFactCompanyBranchId || ''
      })
    }
  }, [station, isOpen, reset])

  const onSubmit = async (data: LinkFuelFactFormData) => {
    try {
      // Get current station data first
      const currentStation = await fillingStationsService.getById(stationId)
      const stationData = currentStation.data
      
      if (!stationData) {
        throw new Error('Station data not found')
      }
      
      // Update only FuelFact fields while keeping other data intact
      await fillingStationsService.update(stationId, {
        businessName: stationData.businessName || '',
        branchName: stationData.branchName || '',
        address: stationData.address || '',
        city: stationData.city || '',
        state: stationData.state || '',
        latitude: stationData.latitude || 0,
        longitude: stationData.longitude || 0,
        accountManagerAccountId: stationData.accountManagerAccountId || '',
        fuelFactCompanyId: data.fuelFactCompanyId,
        fuelFactCompanyBranchId: data.fuelFactCompanyBranchId,
        openingTimeHour: stationData.openingTimeHour || '',
        closingTimeHour: stationData.closingTimeHour || '',
        products: stationData.products || [],
        tanks: stationData.tanks || []
      })
      
      handleSuccess('FuelFact information updated successfully')
      onSuccess()
      onClose()
    } catch (error: any) {
      handleError(error)
    }
  }

  if (!isOpen) return null

  return (
    <ModalWrapper showModal={isOpen} closeModal={onClose} title="Link FuelFact" hideHeader={false}>
      <form onSubmit={handleSubmit(onSubmit)} className="bx-link-fuelfact-form">
        <div className="bx-form-group">
          <label htmlFor="fuelFactCompanyId" className="bx-form-label">
            FuelFact Company ID
          </label>
          <input
            id="fuelFactCompanyId"
            type="text"
            className={`bx-form-input ${errors.fuelFactCompanyId ? 'error' : ''}`}
            placeholder="Enter FuelFact Company ID"
            {...register('fuelFactCompanyId', {
              required: 'FuelFact Company ID is required'
            })}
          />
          {errors.fuelFactCompanyId && (
            <span className="bx-form-error">
              {errors.fuelFactCompanyId.message}
            </span>
          )}
        </div>

        <div className="bx-form-group">
          <label htmlFor="fuelFactCompanyBranchId" className="bx-form-label">
            FuelFact Company Branch ID
          </label>
          <input
            id="fuelFactCompanyBranchId"
            type="text"
            className={`bx-form-input ${errors.fuelFactCompanyBranchId ? 'error' : ''}`}
            placeholder="Enter FuelFact Company Branch ID"
            {...register('fuelFactCompanyBranchId', {
              required: 'FuelFact Company Branch ID is required'
            })}
          />
          {errors.fuelFactCompanyBranchId && (
            <span className="bx-form-error">
              {errors.fuelFactCompanyBranchId.message}
            </span>
          )}
        </div>

        <div className="bx-form-actions">  
          <button
            type="submit"
            className="btn btn-default w-100 py-2"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Updating...' : 'Update FuelFact'}
          </button>
        </div>
      </form>
    </ModalWrapper>
  )
}

export default LinkFuelFactModal
